import React, { useCallback, useEffect, useState } from 'react';

// Banquet Rate Variance Report — shows where your own banquet rates sit
// vs the market, per event_type per month, with optional AI-generated
// plain-language insights. Backend: GET /api/v1/ai/banquet-rate-variance/report.

const sectionStyle = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '1rem 1.2rem',
  marginTop: '0.7rem',
};

const positionStyle = (pos) => {
  switch (pos) {
    case 'BELOW_MARKET': return { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5', label: 'Below market' };
    case 'AT_MARKET': return { bg: '#e0f2fe', text: '#075985', border: '#7dd3fc', label: 'At market' };
    case 'ABOVE_MARKET': return { bg: '#dcfce7', text: '#166534', border: '#86efac', label: 'Above market' };
    case 'PREMIUM': return { bg: '#fef9c3', text: '#854d0e', border: '#fde047', label: 'Premium' };
    default: return { bg: '#f3f4f6', text: '#475569', border: '#e5e7eb', label: 'No data' };
  }
};

const fmtCurrency = (n) => {
  if (n == null || n === 0) return '—';
  return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const fmtPct = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
};

const fmtMonth = (ym) => {
  if (!ym) return '—';
  const d = new Date(`${ym}-01T00:00:00`);
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

function BanquetVarianceReport({ token, selectedProperty, onNavigate }) {
  const [days, setDays] = useState(90);
  const [eventType, setEventType] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      const qs = new URLSearchParams({ days: String(days) });
      if (eventType) qs.set('event_type', eventType);
      const res = await fetch(`/api/v1/ai/banquet-rate-variance/report?${qs.toString()}`, { headers: headers() });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Failed to load variance report');
      setData(body);
    } catch (e) {
      setError(e?.message || 'Failed to load');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty?.id, days, eventType, headers]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedProperty?.id, days, eventType]);

  if (!selectedProperty?.id) {
    return (
      <div style={{ padding: '1rem', maxWidth: 1100 }}>
        <h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Banquet Variance Report</h2>
        <p style={{ color: '#6b7280' }}>Please select a property to view the variance report.</p>
      </div>
    );
  }

  const summary = data?.summary || {};
  const rows = Array.isArray(data?.rows) ? data.rows : [];

  const summaryCards = [
    { key: 'BELOW_MARKET', label: 'Below market' },
    { key: 'AT_MARKET', label: 'At market' },
    { key: 'ABOVE_MARKET', label: 'Above market' },
    { key: 'PREMIUM', label: 'Premium' },
    { key: 'NO_DATA', label: 'No competitor data' },
  ];

  return (
    <div style={{ padding: '1rem', maxWidth: 1300 }}>
      <div style={{ marginBottom: '0.6rem' }}>
        <h2 style={{ margin: 0, color: 'var(--theme-primary)' }}>Banquet Variance Report</h2>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.85rem' }}>
          Where your banquet rates sit versus the market, per event type per month. AI-generated insights when enabled.
        </p>
      </div>

      {error ? <div className="error-banner" style={{ marginBottom: '0.6rem' }}>{error}</div> : null}

      {/* Filters */}
      <div style={{ ...sectionStyle, display: 'flex', gap: '0.9rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 4 }}>Window</div>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            style={{ padding: '0.4rem 0.6rem', borderRadius: 6, border: '1px solid #cbd5e0' }}>
            {[30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>{d} days</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 4 }}>Event type</div>
          <select value={eventType} onChange={(e) => setEventType(e.target.value)}
            style={{ padding: '0.4rem 0.6rem', borderRadius: 6, border: '1px solid #cbd5e0' }}>
            <option value="">All event types</option>
            <option value="WEDDING">Wedding</option>
            <option value="CORPORATE">Corporate</option>
            <option value="SOCIAL">Social</option>
            <option value="GENERAL">General</option>
          </select>
        </div>
        <button onClick={load} disabled={loading}
          style={{ padding: '0.45rem 0.9rem', border: '1px solid #cbd5e0', borderRadius: 6, background: '#fff', color: '#374151', cursor: loading ? 'wait' : 'pointer', fontWeight: 600, marginLeft: 'auto' }}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
        {summaryCards.map((s) => {
          const ps = positionStyle(s.key);
          const count = Number(summary[s.key] || 0);
          return (
            <div key={s.key} style={{ background: ps.bg, color: ps.text, border: `1px solid ${ps.border}`, padding: '0.6rem 0.9rem', borderRadius: 8, minWidth: 130 }}>
              <div style={{ fontSize: '0.76rem', opacity: 0.85 }}>{s.label}</div>
              <div style={{ fontSize: '1.35rem', fontWeight: 700 }}>{count}</div>
            </div>
          );
        })}
      </div>

      {/* AI insight banner */}
      {data && !data.ai_insights_included ? (
        <div style={{ ...sectionStyle, background: '#fffbeb', border: '1px solid #fde68a', color: '#854d0e', fontSize: '0.85rem' }}>
          AI insights are off — showing variance numbers only. Enable <b>Banquet Rate Variance</b> in AI Features Settings to add plain-language explanations to each row.
        </div>
      ) : null}

      {/* Table */}
      <div style={sectionStyle}>
        {loading && rows.length === 0 ? (
          <div style={{ color: '#6b7280' }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ color: '#6b7280' }}>No rate data in this window. Add own and competitor rates in <b>Competitor Intelligence</b> to populate this report.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary-dark, #1e3a5f)', color: '#fff' }}>
                  <th style={{ textAlign: 'left', padding: '0.55rem' }}>Month</th>
                  <th style={{ textAlign: 'left', padding: '0.55rem' }}>Event Type</th>
                  <th style={{ textAlign: 'right', padding: '0.55rem' }}>Own (Veg)</th>
                  <th style={{ textAlign: 'right', padding: '0.55rem' }}>Market avg</th>
                  <th style={{ textAlign: 'right', padding: '0.55rem' }}>Market range</th>
                  <th style={{ textAlign: 'right', padding: '0.55rem' }}>Δ% vs market</th>
                  <th style={{ textAlign: 'left', padding: '0.55rem' }}>Position</th>
                  <th style={{ textAlign: 'left', padding: '0.55rem' }}>AI insight</th>
                  <th style={{ textAlign: 'left', padding: '0.55rem' }}>Drill-down</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const ps = positionStyle(r.position);
                  return (
                    <tr key={`${r.event_type}-${r.month}-${i}`} style={{ borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                      <td style={{ padding: '0.55rem', fontWeight: 600 }}>{fmtMonth(r.month)}</td>
                      <td style={{ padding: '0.55rem', fontWeight: 600 }}>{r.event_type}</td>
                      <td style={{ padding: '0.55rem', textAlign: 'right' }}>{fmtCurrency(r.own_veg)}</td>
                      <td style={{ padding: '0.55rem', textAlign: 'right' }}>{fmtCurrency(r.market_avg_veg)}</td>
                      <td style={{ padding: '0.55rem', textAlign: 'right', fontSize: '0.78rem', color: '#6b7280' }}>
                        {r.market_min_veg ? `${fmtCurrency(r.market_min_veg)} – ${fmtCurrency(r.market_max_veg)}` : '—'}
                      </td>
                      <td style={{ padding: '0.55rem', textAlign: 'right', fontWeight: 700, color: r.variance_pct == null ? '#6b7280' : r.variance_pct >= 5 ? '#166534' : r.variance_pct <= -5 ? '#991b1b' : '#1e40af' }}>
                        {fmtPct(r.variance_pct)}
                      </td>
                      <td style={{ padding: '0.55rem' }}>
                        <span style={{ display: 'inline-block', background: ps.bg, color: ps.text, border: `1px solid ${ps.border}`, padding: '0.15rem 0.55rem', borderRadius: 4, fontSize: '0.78rem', fontWeight: 700 }}>
                          {ps.label}
                        </span>
                      </td>
                      <td style={{ padding: '0.55rem', maxWidth: 320, fontSize: '0.82rem', color: '#374151' }}>{r.insight || '—'}</td>
                      <td style={{ padding: '0.55rem' }}>
                        <button onClick={() => { if (typeof onNavigate === 'function') onNavigate('banquet-intelligence'); }}
                          style={{ padding: '0.3rem 0.6rem', border: '1px solid #cbd5e0', borderRadius: 4, background: '#fff', color: '#1e40af', cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem' }}>
                          Open rates →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: '0.8rem', fontSize: '0.78rem', color: '#6b7280' }}>
        Variance is computed against your competitor rate book in the same window. Rows with no competitor data are marked "No data" — add competitors in <b>Competitor Intelligence</b> to enable comparison.
      </div>
    </div>
  );
}

export default BanquetVarianceReport;
